// ═══════════════════════════════════════
// TEMPO-PICKER — Dropdown sélecteur de tempo
// ═══════════════════════════════════════

import { updateHeaderLine } from './header-form.js';
import { highlight } from './editor.js';
import { render } from './renderer.js';

// ── Durées de notes disponibles ───────────────────────────────────────────────

const NOTE_UNITS = [
    { abc: '1/16', symbol: '𝅘𝅥𝅯',  label: 'Double croche' },
    { abc: '3/32', symbol: '𝅘𝅥𝅯.', label: 'Double croche pointée' },
    { abc: '1/8',  symbol: '♪',    label: 'Croche' },
    { abc: '3/16', symbol: '♪.',   label: 'Croche pointée' },
    { abc: '1/4',  symbol: '♩',    label: 'Noire' },
    { abc: '3/8',  symbol: '♩.',   label: 'Noire pointée' },
    { abc: '1/2',  symbol: '𝅗𝅥',   label: 'Blanche' },
    { abc: '3/4',  symbol: '𝅗𝅥.',  label: 'Blanche pointée' },
    { abc: '1/1',  symbol: '𝅝',    label: 'Ronde' },
];

function _symbolFromUnit(abc) {
    return NOTE_UNITS.find(n => n.abc === abc)?.symbol ?? abc;
}

// ── Parsing ───────────────────────────────────────────────────────────────────

function _fracToFloat(str) {
    const parts = (str || '').trim().split('/');
    if (parts.length === 2) return parseInt(parts[0]) / parseInt(parts[1]);
    return parseFloat(str) || 0;
}

function _noteFromDuration(d) {
    if (d <= 1/32)  return '1/32';
    if (d <= 3/32)  return '3/32';
    if (d <= 1/8)   return '1/8';
    if (d <= 3/16)  return '3/16';
    if (d <= 1/4)   return '1/4';
    if (d <= 3/8)   return '3/8';
    if (d <= 1/2)   return '1/2';
    if (d <= 3/4)   return '3/4';
    return '1/1';
}

function _parseQ(value) {
    const m = (value || '').match(/^([^=]+)=(\d+)$/);
    if (m) {
        const rawUnit = m[1].trim();
        const bpm     = parseInt(m[2]) || 80;
        const d       = _fracToFloat(rawUnit);
        const unit    = d > 0 ? _noteFromDuration(d) : '1/4';
        return { unit, bpm };
    }
    const bpmOnly = parseInt(value);
    if (!isNaN(bpmOnly)) return { unit: '1/4', bpm: bpmOnly };
    return { unit: '1/4', bpm: 80 };
}

// ── État éditeur ──────────────────────────────────────────────────────────────

let _unit = '1/4';
let _bpm  = 80;

// ── Rendu du dropdown ─────────────────────────────────────────────────────────

function _renderFor(state, onCommit) {
    const dd = document.getElementById('tempo-picker-dropdown');
    if (!dd) return;

    let html = `<div class="tp-note-row">`;
    NOTE_UNITS.forEach(n => {
        const sel = n.abc === state.unit ? ' selected' : '';
        html += `<button class="tp-note${sel}" data-abc="${n.abc}" title="${n.label}">${n.symbol}</button>`;
    });
    html += `</div>`;
    html += `<div class="tp-bpm-row">
        <span class="tp-equals">=</span>
        <input type="number" id="tp-bpm" min="1" max="400" value="${state.bpm}">
    </div>`;

    dd.innerHTML = html;

    dd.querySelectorAll('.tp-note').forEach(btn => {
        btn.addEventListener('mousedown', e => {
            e.preventDefault();
            e.stopPropagation();
            state.unit = btn.dataset.abc;
            onCommit(state);
            _renderFor(state, onCommit);
        });
    });

    const bpmEl = document.getElementById('tp-bpm');
    bpmEl.addEventListener('input', () => {
        state.bpm = parseInt(bpmEl.value) || 80;
        onCommit(state);
    });
}

// ── Positionnement ────────────────────────────────────────────────────────────

let _activeTriggerId = null;

function _position(triggerId) {
    const trigger = document.getElementById(triggerId);
    const dd      = document.getElementById('tempo-picker-dropdown');
    if (!trigger || !dd) return;

    const rect = trigger.getBoundingClientRect();
    dd.style.top  = (rect.bottom + window.scrollY + 4) + 'px';
    dd.style.left = (rect.left   + window.scrollX)     + 'px';

    requestAnimationFrame(() => {
        const r = dd.getBoundingClientRect();
        if (r.right > window.innerWidth - 8) {
            dd.style.left = (window.innerWidth - r.width - 8) + 'px';
        }
        if (r.bottom > window.innerHeight - 8) {
            dd.style.top = (trigger.getBoundingClientRect().top + window.scrollY - r.height - 4) + 'px';
        }
    });
}

function _close() {
    const dd = document.getElementById('tempo-picker-dropdown');
    if (dd) dd.style.display = 'none';
    _activeTriggerId = null;
}

// ── Éditeur : commit dans l'ABC ───────────────────────────────────────────────

function _commitEditor(state) {
    _unit = state.unit;
    _bpm  = state.bpm;
    const value = `${_unit}=${_bpm}`;
    const trigger = document.getElementById('hf-tempo-trigger');
    if (trigger) trigger.textContent = `${_symbolFromUnit(_unit)}=${_bpm}`;
    updateHeaderLine('Q', value);
    highlight();
    render();
}

// ── API publique ──────────────────────────────────────────────────────────────

export function setTempoPickerValue(value) {
    const parsed = _parseQ(value);
    _unit = parsed.unit;
    _bpm  = parsed.bpm;
    const trigger = document.getElementById('hf-tempo-trigger');
    if (trigger) trigger.textContent = `${_symbolFromUnit(_unit)}=${_bpm}`;
}

export function initTempoPicker() {
    const triggerId = 'hf-tempo-trigger';
    const trigger   = document.getElementById(triggerId);
    if (!trigger) return;

    trigger.addEventListener('click', e => {
        e.stopPropagation();
        const dd = document.getElementById('tempo-picker-dropdown');
        if (dd && dd.style.display === 'block' && _activeTriggerId === triggerId) {
            _close();
        } else {
            _activeTriggerId = triggerId;
            const state = { unit: _unit, bpm: _bpm };
            _renderFor(state, _commitEditor);
            _position(triggerId);
            dd.style.display = 'block';
        }
    });

    document.addEventListener('mousedown', e => {
        const dd = document.getElementById('tempo-picker-dropdown');
        if (!dd) return;
        const activeTrigger = _activeTriggerId ? document.getElementById(_activeTriggerId) : null;
        if (!dd.contains(e.target) && e.target !== activeTrigger) _close();
    });
}

/**
 * Init pour un trigger externe (modale).
 * onCommit(value) est appelé à chaque changement (value = "1/4=80").
 */
export function initTempoPickerFor(triggerId, initialValue, onCommit) {
    const parsed = _parseQ(initialValue || '1/4=80');
    const state  = { unit: parsed.unit, bpm: parsed.bpm };

    const trigger = document.getElementById(triggerId);
    if (trigger) trigger.textContent = `${_symbolFromUnit(state.unit)}=${state.bpm}`;

    const fresh = trigger?.cloneNode(true);
    if (trigger && fresh) trigger.parentNode.replaceChild(fresh, trigger);

    fresh?.addEventListener('click', e => {
        e.stopPropagation();
        const dd = document.getElementById('tempo-picker-dropdown');
        if (dd && dd.style.display === 'block' && _activeTriggerId === triggerId) {
            _close();
        } else {
            _activeTriggerId = triggerId;
            _renderFor(state, (s) => {
                const value   = `${s.unit}=${s.bpm}`;
                const trig    = document.getElementById(triggerId);
                if (trig) trig.textContent = `${_symbolFromUnit(s.unit)}=${s.bpm}`;
                onCommit(value);
            });
            _position(triggerId);
            const dd2 = document.getElementById('tempo-picker-dropdown');
            if (dd2) dd2.style.display = 'block';
        }
    });
}
